import React, { useState, useMemo, useEffect, useRef } from "react";
//import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getReferences, uploadExcel, updateStandards, updateDomain, createStandard, deleteStandard, createDomain, deleteDomain, getAllDomains } from "../../services/reference";
import i18n from "../../i18n";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./references.css";
import * as XLSX from 'xlsx';
import { TfiImport } from "react-icons/tfi";
import { CiExport } from "react-icons/ci";
import { FiPlus, FiTrash2, FiEdit, FiChevronRight, FiChevronDown, FiSearch } from "react-icons/fi";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import LoadingScreen from "src/Loading/LoadingScreen";
import { getUserInfo, getUserRolePermissions } from '../../services/userServicer';
import { useSearchQuery } from "src/hook/useSearchQuery";
import { useNavigate } from "react-router-dom";
import LoadingButton from "@/components/Buttons/LoadingButton";

interface StandardsData {
  id?: string;
  code: string;
  name: string;
  domains: Domain[];
}

interface Domain {
  id: string;
  code: string;
  en_name: string;
  ar_name: string;
  parentId: string | null;
  subdomains: Subdomain[];
}

interface Subdomain {
  id: string;
  code: string;
  en_name: string;
  ar_name: string;
}

type LoadingMode = 'global' | 'table';

const StandardsSupCompliance: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<StandardsData[]>([]);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [perPage, setPerPage] = useState<number>(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [sortConfig, setSortConfig] = useState<{ key: keyof StandardsData; direction: "ascending" | "descending" | null; }>({ key: "code", direction: null });
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
 // const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalEnName, setModalEnName] = useState("");
  const [modalArName, setModalArName] = useState("");
  const [modalId, setModalId] = useState<string | null>(null);
  const [modalType, setModalType] = useState<"standard" | "domain" | "subdomain" | null>(null);
  const [globalLoading, setGlobalLoading] = useState<boolean>(true);
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [userInfo, setUserInfo] = useState<any>(null);
  const [permissions, setPermissions] = useState<any>({ create: true, edit: true, delete: true, show: true });
  const [notAllowed, setNotAllowed] = useState(false);
  const [permissionsChecked, setPermissionsChecked] = useState(false);
  const didFirstLoad = useRef<boolean>(false);

  // Create modal state
  const [createOpen, setCreateOpen] = useState(false);
  const [createType, setCreateType] = useState<"standard" | "domain" | "subdomain" | null>(null);
  const [createStandardName, setCreateStandardName] = useState("");
  const [createStandardDesc, setCreateStandardDesc] = useState("");
  const [createEnName, setCreateEnName] = useState("");
  const [createArName, setCreateArName] = useState("");
  const [selectedStandardForCreate, setSelectedStandardForCreate] = useState<string | null>(null);
  const [selectedParentForCreate, setSelectedParentForCreate] = useState<string | null>(null);
  const [createEnDesc, setCreateEnDesc] = useState("");
  const [createArDesc, setCreateArDesc] = useState("");
  const [createLink, setCreateLink] = useState("");
  const [allDomains, setAllDomains] = useState<Domain[]>([]);
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set());
  const navigate = useNavigate();

  // Custom delete confirmation modal state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<{ type: 'standard' | 'domain' | 'subdomain'; id: string; name?: string } | null>(null);

  const openConfirm = (type: 'standard' | 'domain' | 'subdomain', id: string, name?: string) => {
    setConfirmTarget({ type, id, name });
    setConfirmOpen(true);
  };
  const closeConfirm = () => {
    setConfirmOpen(false);
    setConfirmTarget(null);
  };


  const toggleDomainExpansion = (domainId: string) => {
    setExpandedDomains(prev => {
      const next = new Set(prev);
      if (next.has(domainId)) next.delete(domainId); else next.add(domainId);
      return next;
    });
  };

  const expandAllDomains = () => {
    const allTopLevel = new Set<string>();
    data.forEach(s => s.domains?.filter(d => !d.parentId).forEach(d => allTopLevel.add(d.id)));
    setExpandedDomains(allTopLevel);
  };

  const collapseAllDomains = () => setExpandedDomains(new Set());

  // Use the search query hook
  const { searchQuery, searchResultType, clearSearchQuery } = useSearchQuery();


const fetchStandardsData = async (page?: number, query?: string, loadingMode: LoadingMode = 'table') => {
  const pageToUse = page !== undefined ? page : currentPage;
  const queryToUse = query !== undefined ? query : debouncedSearchTerm;
  
  if (loadingMode === 'global') {
    setGlobalLoading(true);
  } else {
    setTableLoading(true);
  }
  
  try {
    const response = await getReferences({
      query: queryToUse,
      perPage,
      pageNo: pageToUse,
    });

    if (response && response.success) {
      setData(response.data || []);
      setTotalPages(Math.ceil((response.totalCount || 0) / perPage));
    } else {
      if (response && response.data?.length === 0) {
        setData([]);
        setTotalPages(0);
      } else {
        toast.error(t("Standards_Page.error_fetching_data") || "Failed to fetch standards data");
      }
    }

  } catch (error: any) {
    console.error("Error while fetching standards:", error);
    toast.error(t("Standards_Page.network_error") || "Network error while fetching standards data");
  } finally {
    if (loadingMode === 'global') {
      setGlobalLoading(false);
    } else {
      setTableLoading(false);
    }
  }
};

  const saveEdit = async () => {
    try {
      if (modalType === "standard") {
        const response = await updateStandards(modalId, modalTitle);
        if(response.success){
        fetchStandardsData(currentPage, debouncedSearchTerm, 'table');
        toast.success("Standard updated successfully");
        setModalOpen(false);
      } else {
        toast.error("Error updating standards");
        }
      } else if (modalType === "domain") {
        const response = await updateDomain(modalId as string, { en_name: modalEnName, ar_name: modalArName });
        if(response.success){
          fetchStandardsData(currentPage, debouncedSearchTerm, 'table');
          toast.success("Domain updated successfully");
          setModalOpen(false);
        } else {
          toast.error("Error updating domain");
        }
      } else if (modalType === "subdomain") {
        const response = await updateDomain(modalId as string, { en_name: modalEnName, ar_name: modalArName });
        if(response.success){
          fetchStandardsData(currentPage, debouncedSearchTerm, 'table');
          toast.success("Subdomain updated successfully");
          setModalOpen(false);
        } else {
          toast.error("Error updating subdomain");
        }
      }
    } catch (error) {
      toast.error("Error updating item");
    }
  }

  useEffect(() => {
    const fetchPermissions = async () => {
      let allowed = false;
      try {
        const user = await getUserInfo();
        if (!user?.id) {
          setNotAllowed(true);
          setPermissionsChecked(true);
          setGlobalLoading(false);
          return;
        }
        const response = await getUserRolePermissions(user.id); 
  
        if (response.success) {
          //console.log("Standards permissions response:", response.data);
          // Access the permissions for the "standards" page correctly
          const standardsPagePermission: any = Object.values(response.data.permissions).find(
            (permission: any) => permission.page === "standards"
          );

          //console.log("Standards page permission:", standardsPagePermission);
          // Check if the standardsPagePermission exists and has the 'permissions' object
          if (standardsPagePermission && standardsPagePermission.permissions) {
          //  console.log("Setting permissions:", standardsPagePermission.permissions);
            setPermissions(standardsPagePermission.permissions);
            allowed = !!standardsPagePermission.permissions.show;
            setNotAllowed(!allowed);
          } else {
           // console.log("No standards permissions found, keeping default permissions");
            // Keep default permissions (all true) instead of setting notAllowed
          }
        } else {
         // console.log("Permission response failed, keeping default permissions:", response);
          // Keep default permissions instead of setting notAllowed
        }
      } catch (error) {
        console.error("Error fetching permissions:", error);
        allowed = false;
        setNotAllowed(true);
      } finally {
        setPermissionsChecked(true);
        if (!allowed) {
          setGlobalLoading(false);
        }
      }
    };
  
    fetchPermissions();
  }, []);

  // Debounce search input by 1s
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1000);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  // Trigger backend fetch on page/perPage or debounced search changes
  useEffect(() => {
    if (!permissionsChecked || !permissions?.show) return;
    if (!didFirstLoad.current) {
      didFirstLoad.current = true;
      fetchStandardsData(currentPage, debouncedSearchTerm, 'global');
    } else {
      fetchStandardsData(currentPage, debouncedSearchTerm, 'table');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, perPage, debouncedSearchTerm, permissionsChecked, permissions]);

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm]);

  // Handle search query from global search
  useEffect(() => {
    if (searchQuery && searchResultType === 'standards') {
      setSearchTerm(searchQuery);
      clearSearchQuery();
    }
  }, [searchQuery, searchResultType, clearSearchQuery]);
  
  
  

  const handleDownloadTemplate = () => {
    // Define the data for the Control Standards sheet
    const controlStandardData = [
      ["Name", "Description"],
      ["Standard 1", "Description 1"],
      ["Standard 2", "Description 2"],
      // Add more rows as needed
    ];

    // Define the data for the Domains sheet
    const domainsData = [
      ["Standard Name", "English Name", "Arabic Name", "English Description", "Arabic Description", "Parent Domain Name"],
      ["Standard 1", "Domain 1", "نطاق 1", "description 1", "وصف 1", ""],
      ["Standard 1", "Sub-domain 1", "نطاق فرعي 1", "sub-description 1", "وصف فرعي 1", "Domain 1"],
      ["Standard 2", "Domain 2", "نطاق 2", "description 2", "وصف 2", ""],
      // Add more rows as needed
    ];

    // Define the data for the Controls sheet, including domain and standard relationships
    const controlsData = [
      ["Name", "Description", "Objective", "Standard Name", "Sub Domain Name", "Type"],
      ["Control 1", "Control Description 1", "Objective 1", "Standard 1", "Sub-domain 1", "mandatory"],
      ["Control 2", "Control Description 2", "Objective 2", "Standard 1", "Sub-domain 1"],
    
      // Add more rows as needed
    ];

    // Create a new workbook and add sheets
    const workbook = XLSX.utils.book_new();
    const controlStandardsSheet = XLSX.utils.aoa_to_sheet(controlStandardData);
    const domainsSheet = XLSX.utils.aoa_to_sheet(domainsData);
    const controlsSheet = XLSX.utils.aoa_to_sheet(controlsData);

    // Append sheets to the workbook
    XLSX.utils.book_append_sheet(workbook, controlStandardsSheet, 'Standards');
    XLSX.utils.book_append_sheet(workbook, domainsSheet, 'Domains');
    XLSX.utils.book_append_sheet(workbook, controlsSheet, 'Controls');

    // Write the workbook to a file
    XLSX.writeFile(workbook, 'Control_Domain_Standard_Template.xlsx');
  };

  const handleImportExcel = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
        try {
            const response = await uploadExcel(file);
            toast.success(response.message || "Excel file imported successfully");
            await fetchStandardsData(currentPage, debouncedSearchTerm, 'table');
        } catch (error) {
            toast.error("Error importing Excel file");
        }
        finally {
          try { event.target.value = ''; } catch {}
        }
    }
  };

  const sortedData = useMemo(() => {
    const sortableData = [...data];
    if (sortConfig.direction !== null) {
      const directionFactor = sortConfig.direction === "ascending" ? 1 : -1;
      sortableData.sort((a, b) => {
        const aVal = a[sortConfig.key] as unknown;
        const bVal = b[sortConfig.key] as unknown;
        const aStr = aVal === undefined || aVal === null ? '' : String(aVal);
        const bStr = bVal === undefined || bVal === null ? '' : String(bVal);
        // Natural, case-insensitive, numeric-aware sort (so S2 < S10)
        const cmp = aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: 'base' });
        return cmp * directionFactor;
      });
    }
    return sortableData;
  }, [data, sortConfig]);

  // Backend search applied; visible data is just the sorted page
  const visibleData = useMemo(() => {
    return sortedData;
  }, [sortedData]);

  const toggleSort = (key: keyof StandardsData) => {
    let direction: "ascending" | "descending" | null = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    } else if (sortConfig.key === key && sortConfig.direction === "descending") {
      direction = null;
    }
    setSortConfig({ key, direction });
  };

  const toggleRowExpansion = (index: number) => {
    setExpandedRows((prev) => {
      const newExpandedRows = new Set(prev);
      if (newExpandedRows.has(index)) {
        newExpandedRows.delete(index);
      } else {
        newExpandedRows.add(index);
      }
      return newExpandedRows;
    });
  };

  const toggleModal = (id: any,title: string, code: string, type: "standard" | "domain" | "subdomain" , arName: string) => {
   if(type === "domain" || type === "subdomain"){
    
    setModalEnName(title);
    setModalArName(arName);
   } else {
    setModalTitle(title);
   }
    setModalId(id);
    setModalType(type);
    setModalOpen(!modalOpen);
  };

  const openCreateStandard = () => {
    setCreateType("standard");
    setCreateStandardName("");
    setCreateStandardDesc("");
    setCreateOpen(true);
  };

  const openCreateDomain = (standardId: string) => {
    setCreateType("domain");
    setSelectedStandardForCreate(standardId);
    setCreateEnName("");
    setCreateArName("");
    setCreateEnDesc("");
    setCreateArDesc("");
    setCreateLink("");
    setCreateOpen(true);
  };

  const openCreateSubdomain = (parentId: string) => {
    setCreateType("subdomain");
    setSelectedParentForCreate(parentId);
    
    // Auto-detect the standard from the parent domain
    let standardId: string | null = null;
    for (const standard of data) {
      const foundDomain = standard.domains?.find((d: Domain) => d.id === parentId);
      if (foundDomain) {
        standardId = standard.id || null;
        break;
      }
      // Also check in subdomains (nested structure)
      for (const domain of standard.domains || []) {
        if (domain.subdomains?.some((sd: Subdomain) => sd.id === parentId)) {
          // If parent is a subdomain, find its parent domain's standard
          standardId = standard.id || null;
          break;
        }
      }
      if (standardId) break;
    }
    setSelectedStandardForCreate(standardId);
    
    setCreateEnDesc("");
    setCreateArDesc("");
    setCreateLink("");
    setCreateEnName("");
    setCreateArName("");
    setCreateOpen(true);
  };

  const handleCreateSubmit = async () => {
    try {
      if (createType === "standard") {
        if (!createStandardName.trim()) { toast.error(t("Standards_Page.edit_name")); return; }
        const res = await createStandard({
          name: createStandardName.trim(),
          description: createStandardDesc.trim() || undefined,
        });
        if (res?.success) {
          toast.success(t("Standard created successfully") || "Standard created successfully");
          setCreateOpen(false);
          fetchStandardsData(currentPage, debouncedSearchTerm, 'table');
        } else { toast.error(res?.message || "Error creating standard"); }
      }
      if (createType === "domain") {
        if (!createEnName.trim() || !createArName.trim() || !selectedStandardForCreate) { toast.error(t("Standards_Page.edit_name") || "Missing data"); return; }
        const res = await createDomain({ en_name: createEnName.trim(), ar_name: createArName.trim(), standard_id: selectedStandardForCreate, en_description: createEnDesc || undefined, ar_description: createArDesc || undefined, link: createLink || undefined });
        if (res?.success) {
          toast.success(t("Domain created successfully") || "Domain created successfully");
          setCreateOpen(false);
          fetchStandardsData(currentPage, debouncedSearchTerm, 'table');
        } else { toast.error(res?.message || "Error creating domain"); }
      }
      if (createType === "subdomain") {
        if (!createEnName.trim() || !createArName.trim() || !selectedParentForCreate) { toast.error(t("Standards_Page.edit_name") || "Missing data"); return; }
        const res = await createDomain({ en_name: createEnName.trim(), ar_name: createArName.trim(), parentId: selectedParentForCreate, standard_id: selectedStandardForCreate || undefined, en_description: createEnDesc || undefined, ar_description: createArDesc || undefined, link: createLink || undefined });
        if (res?.success) {
          toast.success(t("Subdomain created successfully") || "Subdomain created successfully");
          setCreateOpen(false);
          fetchStandardsData(currentPage, debouncedSearchTerm, 'table');
        } else { toast.error(res?.message || "Error creating subdomain"); }
      }
    } catch (e: any) {
      toast.error(e?.message || "Error while creating");
    }
  };

  const handleDeleteStandard = (id: string, name?: string) => {
    openConfirm('standard', id, name);
  };

  // Fetch all domains for parent domain select in create subdomain
  useEffect(() => {
    const loadDomains = async () => {
      try {
        const res = await getAllDomains();
        if (res?.data) setAllDomains(res.data);
      } catch (e) { /* ignore */ }
    };
    loadDomains();
  }, []);

  const handleDeleteDomain = (id: string, name?: string) => {
    openConfirm('domain', id, name);
  };

  const confirmDelete = async () => {
    if (!confirmTarget) return;
    if (confirmTarget.type === 'standard') {
      const res = await deleteStandard(confirmTarget.id);
      if (res?.success) { toast.success(t('Deleted successfully') || 'Deleted successfully'); await fetchStandardsData(currentPage, debouncedSearchTerm, 'table'); }
      else { toast.error(res?.message || 'Delete failed'); }
    } else {
      const res = await deleteDomain(confirmTarget.id);
      if (res?.success) { toast.success(t('Deleted successfully') || 'Deleted successfully'); await fetchStandardsData(currentPage, debouncedSearchTerm, 'table'); }
      else { toast.error(res?.message || 'Delete failed'); }
    }
    setConfirmOpen(false);
    setConfirmTarget(null);
  };



  // Navigate to edit a specific control
const handleEditControl = (controlId: string) => {
  if (!controlId) return;
  navigate(`/updatecontrol/${controlId}`);
};
// Show initial global loader before deciding on permission
// This ensures the page always starts with the full-screen loader
if (globalLoading) {
  return (
    <div className="loading-screen" 
    style={{ 
      backgroundColor: 'rgba(255, 255, 255, 0.8)'}}>
                 <div><LoadingScreen/></div>
               </div>
  );
}

  if (notAllowed) {
    return (
      <div className="ref-sup-denied-wrap">
        <div className="ref-sup-denied-card" role="alert">
          {t("You do not have permission to view this page.")}
        </div>
      </div>
    );
  }
  

  return (
    <div className="root ref-sup-page">
      <header className="ref-sup-header">
        <h1 className="ref-sup-title">{t("Standards_Page.page_title_standards")}</h1>
        <p className="ref-sup-subtitle">{t("Standards_Page.page_subtitle")}</p>
      </header>

      <div className="ref-sup-toolbar-card">
        <div className="ref-sup-toolbar-row">
          <div className="ref-sup-toolbar-actions">
            {permissions?.create && (
              <button
                type="button"
                onClick={openCreateStandard}
                className="ref-sup-btn-primary"
                title={String(t("Add New Standard") || "")}
              >
                <FiPlus size={16} aria-hidden />
                {t("Add Standard") || "Add Standard"}
              </button>
            )}
            <input
              type="file"
              accept=".xlsx, .xls"
              onChange={handleImportExcel}
              className="ref-sup-file-input"
              id="import-excel"
            />
            <label htmlFor="import-excel" className="ref-sup-tool-btn ref-sup-tool-btn--outline">
              <TfiImport size={14} aria-hidden />
              {t("Standards_Page.import_excel")}
            </label>
            <button type="button" className="ref-sup-tool-btn ref-sup-tool-btn--muted" onClick={handleDownloadTemplate}>
              <CiExport size={18} aria-hidden />
              {t("Standards_Page.download_excel_template")}
            </button>
          </div>
        </div>
      </div>

      <div className="ref-sup-search-card">
        <div className="ref-sup-search-wrap">
          <FiSearch className="ref-sup-search-icon" size={18} aria-hidden />
          <input
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                setCurrentPage(0);
                fetchStandardsData(0, (searchTerm || "").trim(), "table");
              }
            }}
            placeholder={String(t("Standards_Page.search_placeholder"))}
            className="ref-sup-search-input"
            aria-label={String(t("Standards_Page.search_placeholder"))}
          />
        </div>
      </div>

      <div className="ref-sup-table-panel">
        <div className="table-responsive">
          <table className="styled-table ref-sup-table">
          <thead>
            <tr>
              <th onClick={() => toggleSort("code")} className="sortable-header">
                {t("Standards_Page.Header_Code")}{" "}
                {sortConfig.key === "code" ? (sortConfig.direction === "ascending" ? "🔼" : "🔽") : ""}
              </th>
              <th onClick={() => toggleSort("name")} className="sortable-header">
                {t("Standards_Page.name")}{" "}
                {sortConfig.key === "name" ? (sortConfig.direction === "ascending" ? "🔼" : "🔽") : ""}
              </th>
              <th>{t("Standards_Page.Domains")}</th>
              <th className="ref-sup-th-actions">{t("Standards_Page.Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {tableLoading ? (
              <tr>
                <td colSpan={4}>
                  <div className="ref-sup-table-loading">
                    <div className="spinner-border ref-sup-spinner" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                </td>
              </tr>
            ) : visibleData.length > 0 ? (
              visibleData.map((item: any, index) => (
              <React.Fragment key={item.code}>
                <tr className="clickable-row">
                  <td>
                    {item.code} 
                  </td>
                  <td className="editable-cell">
                  {/* <FaEdit
                     onClick={() => toggleModal(item.id, item.name, item.code, "standard", "")}
                      className="edit-icon" /> */}
                    {item.name} 
                  </td>
                  <td className="expand-icon">
                    <button
                      className="btn-square btn-outline-blue"
                      onClick={() => toggleRowExpansion(index)}
                      title={expandedRows.has(index) ? (t('Collapse') || 'Collapse') : (t('Expand') || 'Expand')}
                      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28 }}
                    >
                      {expandedRows.has(index) ? <FiChevronDown size={16}/> : <FiChevronRight size={16}/>} 
                    </button>
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      <button
                        className="btn-square btn-outline-blue"
                        onClick={() => toggleModal(item.id, item.name, item.code, "standard", "")}
                        title={t('Edit') || 'Edit'}
                        disabled={!permissions?.edit}
                        style={{ opacity: !permissions?.edit ? 0.4 : 1, display:'inline-flex', alignItems:'center', justifyContent:'center', width:28, height:28 }}
                      >
                        <FiEdit size={15}/>
                      </button>
                      <button
                        className="btn-square btn-outline-red"
                        onClick={() => handleDeleteStandard(item.id, item.name)}
                        title={t('Delete') || 'Delete'}
                        disabled={!permissions?.delete}
                        style={{ opacity: !permissions?.delete ? 0.4 : 1, display:'inline-flex', alignItems:'center', justifyContent:'center', width:28, height:28 }}
                      >
                        <FiTrash2 size={15}/>
                      </button>
                      <button
                        className="btn-square btn-outline-green"
                        onClick={() => openCreateDomain(item.id)}
                        title={t('Add Domain') || 'Add Domain'}
                        disabled={!permissions?.create}
                        style={{ opacity: !permissions?.create ? 0.4 : 1, display:'inline-flex', alignItems:'center', justifyContent:'center', width:28, height:28 }}
                      >
                        <FiPlus size={15}/>
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedRows.has(index) && (
                  <tr>
                    <td colSpan={4}>
                      <div
                        className={`ref-sup-expanded${i18n.language === "ar" ? " ref-sup-expanded--rtl" : ""}`}
                      >
                        <div className="ref-sup-expanded-toolbar">
                          <strong>{t("Standards_Page.Domains")}</strong>
                          <div className="ref-sup-expanded-toolbar-btns">
                            <button type="button" className="ref-sup-mini-btn" onClick={expandAllDomains}>
                              {t("Expand All")}
                            </button>
                            <button type="button" className="ref-sup-mini-btn" onClick={collapseAllDomains}>
                              {t("Collapse All")}
                            </button>
                          </div>
                        </div>
                        {item.domains && item.domains.length > 0 ? (
                          <ul className="domain-list" style={{ listStyle: 'none', padding: 0 }}>
                            {item.domains
                              .filter((domain: Domain) => !domain.parentId)
                              .map((domain: Domain, domainIndex: number) => (
                                <li key={domainIndex} className={`domain-item${expandedDomains.has(domain.id) ? ' expanded' : ''}`} style={{ marginBottom: '10px' }}>
                                  <div className="domain-header">
                                    <div className="d-flex align-items-center gap-2" style={{flex: 1}}>
                                      {domain.subdomains?.length ? (
                                        <button
                                          onClick={() => toggleDomainExpansion(domain.id)}
                                          className={`btn-square btn-outline-blue`}
                                          title={expandedDomains.has(domain.id) ? (t('Collapse') || 'Collapse') : (t('Expand') || 'Expand')}
                                          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28 }}
                                        >
                                          {expandedDomains.has(domain.id) ? <FiChevronDown size={16}/> : <FiChevronRight size={16}/>} 
                                        </button>
                                      ) : null}
                                      <h6 className="domain-title mb-0">
                                    {i18n.language === "ar" ? domain.ar_name : domain.en_name}
                                        {domain.subdomains?.length ? (
                                          <span className="text-muted small ms-2">({domain.subdomains.length} {t('subdomains') || 'subdomains'})</span>
                                        ) : null}
                                      </h6>
                                    </div>
                                    <div className="action-btn-group d-flex gap-2">
                                      <button className="btn-square btn-outline-blue" onClick={() => toggleModal(domain.id, domain.en_name, domain.id, "domain", domain.ar_name)} title={t('Edit') || 'Edit'} disabled={!permissions?.edit} style={{ opacity: !permissions?.edit ? 0.4 : 1, display:'inline-flex', alignItems:'center', justifyContent:'center', width:28, height:28 }}>
                                        <FiEdit size={15}/>
                                      </button>
                                      <button className="btn-square btn-outline-red" onClick={() => handleDeleteDomain(domain.id, i18n.language === 'ar' ? domain.ar_name : domain.en_name)} title={t('Delete') || 'Delete'} disabled={!permissions?.delete} style={{ opacity: !permissions?.delete ? 0.4 : 1, display:'inline-flex', alignItems:'center', justifyContent:'center', width:28, height:28 }}>
                                        <FiTrash2 size={15}/>
                                      </button>
                                      <button className="btn-square btn-outline-green" onClick={() => openCreateSubdomain(domain.id)} title={t('Add Subdomain') || 'Add Subdomain'} disabled={!permissions?.create} style={{ opacity: !permissions?.create ? 0.4 : 1, display:'inline-flex', alignItems:'center', justifyContent:'center', width:28, height:28 }}>
                                        <FiPlus size={15}/>
                                      </button>
                                    </div>
                                  </div>
                                  
                                  {domain.subdomains && domain.subdomains.length > 0 && expandedDomains.has(domain.id) && (
                                    <ul
                                      className="subdomain-list"
                                      style={{
                                        listStyleType: 'circle',
                                        margin: i18n.language === "ar" ? '0 20px 0 0' : '0 0 0 20px',
                                      }}
                                    >
                                      {domain.subdomains.map((subDomain: Subdomain, subDomainIndex: number) => (
                                        <li key={subDomainIndex} className="subdomain-item">
                                          <div className="subdomain-content d-flex align-items-center justify-content-between">
                                            <h6 className="subdomain-title mb-0" style={{ marginRight: 8 }}>
                                              {i18n.language === "ar" ? subDomain.ar_name : subDomain.en_name}
                                            </h6>
                                            <div className="action-btn-group d-flex gap-2">
                                              <button className="btn-square btn-outline-blue" onClick={() => toggleModal(subDomain.id, subDomain.en_name, subDomain.id, "subdomain", subDomain.ar_name)} title={t('Edit') || 'Edit'} disabled={!permissions?.edit} style={{ opacity: !permissions?.edit ? 0.4 : 1, display:'inline-flex', alignItems:'center', justifyContent:'center', width:28, height:28 }}><FiEdit size={15}/></button>
                                              <button className="btn-square btn-outline-red" onClick={() => handleDeleteDomain(subDomain.id, i18n.language === 'ar' ? subDomain.ar_name : subDomain.en_name)} title={t('Delete') || 'Delete'} disabled={!permissions?.delete} style={{ opacity: !permissions?.delete ? 0.4 : 1, display:'inline-flex', alignItems:'center', justifyContent:'center', width:28, height:28 }}><FiTrash2 size={15}/></button>
                                              {permissions?.create && (
                                                <button
                                                  type="button"
                                                  className="btn-square btn-outline-green"
                                                  onClick={() => navigate(`/createcontrol/new?subdomainId=${subDomain.id}`)}
                                                  title={t('Add Control') || 'Add Control'}
                                                  style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:28, height:28 }}
                                                >
                                            <FiPlus size={15}/>
                                                </button>
                                              )}
                                            </div>
                                          </div>
                                          {/* Controls list under a subdomain */}
                                          {Array.isArray((subDomain as any).subdomainControlDomains) && (subDomain as any).subdomainControlDomains.length > 0 && (
                                              <div className="ref-sup-controls-block">
                                              <div className="ref-sup-controls-heading">
                                                {t("Standards_Page.controls_heading")}
                                              </div>
                                              {(subDomain as any).subdomainControlDomains.map((scd: any) => (
                                                <div
                                                  key={scd.id}
                                                  className="ref-sup-control-row"
                                                >
                                                  <div className="ref-sup-control-name">
                                                    {scd?.control?.name || scd?.control_id}
                                                  </div>
                                                  <button
                                                    type="button"
                                                    className="ref-sup-mini-btn"
                                                    title={String(t("Edit Control") || "")}
                                                    onClick={() => handleEditControl(scd?.control?.id || scd?.control_id)}
                                                  >
                                                    ✏
                                                  </button>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </li>
                              ))}
                          </ul>
                        ) : (
                          <span>{t("Standards_Page.no_domains_available")}</span>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))
            ) : (
              <tr>
                <td colSpan={4} className="ref-sup-empty-cell">
                  {t("Standards_Page.no_data_available")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
        <nav className="ref-sup-pagination" aria-label={String(t("Standards_Page.page_title_standards"))}>
          <button
            type="button"
            className="ref-sup-page-btn"
            onClick={() => setCurrentPage(0)}
            disabled={currentPage === 0}
          >
            «
          </button>
          <button
            type="button"
            className="ref-sup-page-btn"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
            disabled={currentPage === 0}
          >
            ‹ {t("institution.pagination_previous")}
          </button>
          <span className="ref-sup-page-current" aria-current="page">
            {currentPage + 1}
          </span>
          <button
            type="button"
            className="ref-sup-page-btn"
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, Math.max(0, totalPages - 1)))
            }
            disabled={currentPage + 1 >= totalPages || totalPages === 0}
          >
            {t("institution.pagination_next")} ›
          </button>
          <button
            type="button"
            className="ref-sup-page-btn"
            onClick={() => setCurrentPage(Math.max(0, totalPages - 1))}
            disabled={currentPage + 1 >= totalPages || totalPages === 0}
          >
            »
          </button>
          <span className="ref-sup-page-meta">
            {t("Standards_Page.page")} {currentPage + 1} {t("Standards_Page.of")} {Math.max(1, totalPages)}
          </span>
        </nav>
      </div>

      <Modal
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        className="ref-sup-modal"
        contentClassName="ref-sup-modal-content"
        backdropClassName="ref-sup-modal-backdrop"
        centered
      >
        <ModalHeader
          className="ref-sup-modal-header"
          style={{ direction: "ltr" }}
          toggle={() => setModalOpen(!modalOpen)}
        >
          {modalTitle}
        </ModalHeader>
        <ModalBody className="ref-sup-modal-body">
          {modalType === "standard" && <div>
            <label htmlFor="editName">{t("Standards_Page.edit_name")}:</label>
            <input
              type="text"
              id="editName"
              name="editName"
              value={modalTitle}
              onChange={(e) => setModalTitle(e.target.value)}
              className="form-control"
            />
          </div>}
          {(modalType === "domain" || modalType === "subdomain") && <div>
            <label htmlFor="editName">{t("Standards_Page.edit_en_name")}:</label>
            <input
              type="text"
              id="editeNName"
              name="editeNName"
              value={modalEnName}
              onChange={(e) => setModalEnName(e.target.value)}
              className="form-control"
            /> 
            <label htmlFor="editName">{t("Standards_Page.edit_ar_name")}:</label>
            <input
              type="text"
              id="editArName"
              name="editArName"
              value={modalArName}
              onChange={(e) => setModalArName(e.target.value)}
              className="form-control"
            /> 
            </div>}
         
        </ModalBody>
        <ModalFooter className="ref-sup-modal-footer">
          <button type="button" className="ref-sup-tool-btn ref-sup-tool-btn--muted" onClick={() => setModalOpen(false)}>
            {t("Standards_Page.cancel")}
          </button>
          <LoadingButton
            onClick={saveEdit}
            variant="success"
            className="ref-sup-modal-save btn rounded"
            disabled={!permissions?.edit}
          >
            {t("Standards_Page.save")}
          </LoadingButton>
        </ModalFooter>
      </Modal>

      {/* Create Modal */}
      <Modal
        isOpen={createOpen}
        toggle={() => setCreateOpen(!createOpen)}
        className="ref-sup-modal"
        contentClassName="ref-sup-modal-content"
        backdropClassName="ref-sup-modal-backdrop"
        centered
      >
        <ModalHeader
          className="ref-sup-modal-header"
          style={{ direction: "ltr" }}
          toggle={() => setCreateOpen(!createOpen)}
        >
          {createType === "standard" && (t("Add Standard") || "Add Standard")}
          {createType === "domain" && (t("Add Domain") || "Add Domain")}
          {createType === "subdomain" && (t("Add Subdomain") || "Add Subdomain")}
        </ModalHeader>
        <ModalBody className="ref-sup-modal-body">
          {createType === "standard" && (
            <div className="modal-body-professional">
              <div className="form-group-professional">
                <label>{t("Name")}</label>
                <input className="form-control-professional" value={createStandardName} onChange={(e) => setCreateStandardName(e.target.value)} />
              </div>
              <div className="form-group-professional">
                <label>{t("Description")}</label>
                <textarea className="form-control textarea" rows={3} value={createStandardDesc} onChange={(e) => setCreateStandardDesc(e.target.value)} />
              </div>
            </div>
          )}

          {(createType === "domain" || createType === "subdomain") && (
            <div className="modal-body-professional">
              {/* Row: English/Arabic Name */}
              <div className="form-group-professional" style={{ display: 'flex', gap: 12 }}>
                <div style={{ width: '50%' }}>
                  <label>{t("English Name")}</label>
                  <input className="form-control-professional" value={createEnName} onChange={(e) => setCreateEnName(e.target.value)} />
                </div>
                <div style={{ width: '50%' }}>
                  <label>{t("Arabic Name")}</label>
                  <input className="form-control-professional" value={createArName} onChange={(e) => setCreateArName(e.target.value)} />
                </div>
              </div>

              {/* Row: English/Arabic Description */}
              <div className="form-group-professional" style={{ display: 'flex', gap: 12 }}>
                <div style={{ width: '50%' }}>
                  <label>{t("English Description")}</label>
                  <textarea className="form-control textarea" rows={3} value={createEnDesc} onChange={(e) => setCreateEnDesc(e.target.value)} />
                </div>
                <div style={{ width: '50%' }}>
                  <label>{t("Arabic Description")}</label>
                  <textarea className="form-control textarea" rows={3} value={createArDesc} onChange={(e) => setCreateArDesc(e.target.value)} />
                </div>
              </div>

              {/* Link input removed for both domain and subdomain */}
            </div>
          )}
        </ModalBody>
        <ModalFooter className="ref-sup-modal-footer">
          <button type="button" className="ref-sup-tool-btn ref-sup-tool-btn--muted" onClick={() => setCreateOpen(false)}>
            {t("Cancel")}
          </button>
          <LoadingButton
            onClick={handleCreateSubmit}
            variant="success"
            className="ref-sup-modal-save btn rounded"
            disabled={!permissions?.create}
          >
            {t("Create")}
          </LoadingButton>
        </ModalFooter>
      </Modal>
      {/* Custom Delete Confirmation Modal */}
      {confirmOpen && (
        <>
          <div className="ref-sup-confirm-backdrop" onClick={closeConfirm} aria-hidden />
          <div className="ref-sup-confirm-root" role="dialog" aria-modal="true" aria-labelledby="ref-sup-confirm-title">
            <div className="ref-sup-confirm-dialog">
              <div className="ref-sup-confirm-card">
                <div className="ref-sup-confirm-header">
                  <h5 id="ref-sup-confirm-title" className="ref-sup-confirm-title">
                    {t("Confirm Deletion") || "Confirm Deletion"}
                  </h5>
                  <button type="button" className="ref-sup-confirm-close" onClick={closeConfirm} aria-label="Close">
                    ×
                  </button>
                </div>
                <div className="ref-sup-confirm-body">
                  <p className="mb-0">
                    {t("Are you sure you want to delete?") || "Are you sure you want to delete?"}
                    {confirmTarget?.name ? (
                      <>
                        {" "}
                        <strong>{confirmTarget?.name}</strong>
                      </>
                    ) : null}
                  </p>
                </div>
                <div className="ref-sup-confirm-footer">
                  <button type="button" className="ref-sup-tool-btn ref-sup-tool-btn--muted" onClick={closeConfirm}>
                    {t("Cancel") || "Cancel"}
                  </button>
                  <LoadingButton
                    onClick={confirmDelete}
                    variant="danger"
                    className="ref-sup-confirm-delete"
                    loadingText={String(t("Deleting...") || "")}
                  >
                    {t("Delete") || "Delete"}
                  </LoadingButton>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default StandardsSupCompliance;